import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useState, useEffect, useMemo} from 'react'
import Image from 'next/image'
import type {Block, TersedAsset} from '@/_new-code/services/kontent-ai/types'
import {Modal} from '@/_new-code/components/modal/modal'
import {RichTextRenderer} from '@/_new-code/products/flexible-web-toolkit/components/rich-text-renderer'
import {loadImageFromKontentAI} from '@/imageLoader'
import {getBrandColor} from '@/_new-code/products/flexible-web-toolkit/blocks/helpers'
import {getBrandColorClasses} from '@/_new-code/products/pdp/utils/brand-colors'

export type PDPPopupWithRichTextContentItem = IContentItem<{
	buttonText: Elements.TextElement
	icon: Elements.AssetsElement
	description: Elements.RichTextElement
}>

export const PDPPopupWithRichTextBlock: Block<
	PDPPopupWithRichTextContentItem
> = ({block, BlockMapper, globalConfig, page}) => {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const {buttonText, icon, description} = block.elements

	// Get first icon if available
	const iconAsset: TersedAsset | undefined = icon[0]

	// Optimized brand color calculation with memoization
	const {brandColor, brandColorClasses} = useMemo(() => {
		const pageConfig = page.elements.config[0]
		const color = pageConfig ? getBrandColor(pageConfig) : null
		const classes = getBrandColorClasses(color)
		return {brandColor: color, brandColorClasses: classes}
	}, [page.elements.config])
	const handleOpenModal = (): void => {
		setIsModalOpen(true)
	}

	const handleCloseModal = (): void => {
		setIsModalOpen(false)
	}

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent): void => {
			if (e.key === 'Escape' && isModalOpen) {
				handleCloseModal()
			}
		}

		const handleClickOutside = (e: MouseEvent): void => {
			if (isModalOpen) {
				const target = e.target as HTMLElement
				if (target.classList.contains('bg-black/50')) {
					handleCloseModal()
				}
			}
		}

		if (isModalOpen) {
			document.addEventListener('keydown', handleKeyDown)
			document.addEventListener('click', handleClickOutside)
			return () => {
				document.removeEventListener('keydown', handleKeyDown)
				document.removeEventListener('click', handleClickOutside)
			}
		}
	}, [isModalOpen])

	return (
		<div className="inline-block">
			<button
				aria-expanded={isModalOpen}
				aria-haspopup="dialog"
				className={`inline-flex items-center gap-2 rounded border-2 px-3 py-1 text-sm font-bold transition-all duration-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
					brandColorClasses.border
				} bg-white ${
					brandColorClasses.text
				} hover:bg-gray-50 focus:ring-${brandColor || 'elanco-blue'}`}
				onClick={handleOpenModal}
				type="button"
			>
				{iconAsset ? (
					<Image
						alt=""
						className="h-5 w-5 flex-shrink-0"
						height={20}
						loader={loadImageFromKontentAI}
						src={iconAsset.url}
						width={20}
					/>
				) : null}
				{buttonText}
			</button>

			{isModalOpen ? (
				<Modal
					brandColor={brandColor || 'elanco-blue'}
					isOpen={isModalOpen}
					onClose={handleCloseModal}
					size="lg"
					variant="right-slide"
				>
					<div className="prose prose-sm max-w-none">
						<RichTextRenderer
							BlockMapper={BlockMapper}
							element={description}
							globalConfig={globalConfig}
							page={page}
						/>
					</div>
				</Modal>
			) : null}
		</div>
	)
}
